import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../atoms';
import { Stepper } from '../molecules';
import { useOpeningElementsControllerContext, useUserLanguage, useCreateCommunicationContext, useErrorHandlerContext } from '@/customHooks';
import textsData from '@/textData/CreateCommunicationsTexts.json';
import { CreateCommunicationTemplate } from '../templates';
import { pathURL } from '@/components/constants/urls';
import {
  useSendMassiveCommunicationMutation,
  useCreateMassiveCommunicationTemplateMutation,
  UserTypeOption,
  type CreateMassiveCommunicationInput,
} from '@/graphql/generated';

export const CommunicationsPage = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const [currentPage, setCurrentPage] = useState(0);
  const steps = useMemo(() => texts.templateText.map((element) => element.step), [texts]);

  const navigate = useNavigate();
  const { confirmDialogModalOpen } = useOpeningElementsControllerContext();
  const { open: errorHandle } = useErrorHandlerContext();
  const { buttonState, state } = useCreateCommunicationContext();
  const [sendMassiveCommunication, { loading: isSending }] = useSendMassiveCommunicationMutation();
  const [createTemplate] = useCreateMassiveCommunicationTemplateMutation();

  const onFinishHandle = async () => {
    const uploadedFilesIds = state.uploadedFiles.map((f) => f.id);
    const hasPropertyFilter = state.selectedPropertyIds.length > 0;

    const audienceToUserType = (
      t: (typeof state.targetUserTypes)[number] | undefined,
    ): UserTypeOption | null => {
      if (t === 'TENANT') return UserTypeOption.Tenant;
      if (t === 'OWNER') return UserTypeOption.Owner;
      if (t === 'SUPPLIER') return UserTypeOption.Supplier;
      return null;
    };

    const input: CreateMassiveCommunicationInput = {
      title: state.title,
      content: state.message,
      uploadedFilesIds: uploadedFilesIds.length > 0 ? uploadedFilesIds : undefined,
    };

    if (state.excludeRecipientUserIds.length > 0) {
      input.excludeRecipientUserIds = state.excludeRecipientUserIds;
    }

    if (hasPropertyFilter) {
      input.propertyIds = state.selectedPropertyIds;
    }

    const tenantActiveOnly =
      state.onlyActiveContractsSelected && state.targetUserTypes[0] === 'TENANT';

    if (hasPropertyFilter && state.allStakeholdersOnSelectedProperties) {
    } else if (state.allUsersSelected && state.targetUserTypes[0]) {
      const mapped = audienceToUserType(state.targetUserTypes[0]);
      if (mapped) input.recipientUserTypes = [mapped];
    } else if (hasPropertyFilter && tenantActiveOnly) {
      input.recipientUserTypes = [UserTypeOption.Tenant];
    } else if (!hasPropertyFilter && tenantActiveOnly && state.selectedUserIds.length > 0) {
      input.recipientUserIds = state.selectedUserIds;
    } else if (hasPropertyFilter && state.selectedUserIds.length > 0) {
      input.recipientUserIds = state.selectedUserIds;
    } else if (!hasPropertyFilter && state.selectedUserIds.length > 0) {
      input.recipientUserIds = state.selectedUserIds;
    }

    try {
      if (state.mode === 'NEW' && state.title.trim() && state.message.trim()) {
        void createTemplate({
          variables: {
            input: {
              title: state.title,
              content: state.message,
            },
          },
          refetchQueries: ['GetMassiveCommunicationTemplates'],
        });
      }

      await sendMassiveCommunication({
        variables: {
          input,
        },
      });
      confirmDialogModalOpen({
        title: texts.confirmationModal.title,
        description: texts.confirmationModal.description,
      });
      navigate(`/${pathURL.USER}/${pathURL.COMMUNICATIONS}`);
    } catch (e) {
      errorHandle(e);
    }
  };

  return (
    <div>
      <div>
        <div className="flex justify-center py-4">
          <Stepper steps={steps} currentStep={currentPage} />
        </div>
      </div>

      <CreateCommunicationTemplate currentTemplate={currentPage} />

      <div className="my-8 flex justify-center gap-3">
        {currentPage === steps.length - 1 && (
          <div>
            <Button
              secondary
              style={{ backgroundColor: '#00183A', borderColor: '#00183A', color: '#FFFFFF' }}
              onClick={() => navigate(`/${pathURL.USER}/${pathURL.COMMUNICATIONS}`)}
            >
              {userLanguage === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
          </div>
        )}
        <div>
          <Button
            secondary
            onClick={() => {
              if (currentPage === steps.length - 1) {
                setCurrentPage(steps.length - 2);
              } else if (currentPage > 0) {
                setCurrentPage((prev) => prev - 1);
              } else {
                navigate(`/${pathURL.USER}/${pathURL.COMMUNICATIONS}`);
              }
            }}
          >
            {currentPage === steps.length - 1
              ? userLanguage === 'es'
                ? 'Editar mensaje'
                : 'Edit message'
              : texts.cancelButton}
          </Button>
        </div>
        <div>
          <Button
            onClick={() => {
              if (currentPage < steps.length - 1) setCurrentPage((prev) => prev + 1);
              else void onFinishHandle();
            }}
            disabled={buttonState || isSending}
            isLoading={isSending}
          >
            {texts.confirmButton}
          </Button>
        </div>
      </div>
    </div>
  );
};

